import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useParams } from "react-router-dom";
import { ChevronDown, ChevronRight, Clock, Loader2, Play, Plus, Trash2 } from "lucide-react";
import { api, ApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RelativeTime } from "@/components/RelativeTime";

type ProductionState = "ok" | "no_production" | "static_output";
type RunOutcome = "success" | "failed" | "timeout" | "skipped_overlap" | "skipped_no_prod";

interface LastRun {
  outcome: RunOutcome;
  httpStatus: number | null;
  durationMs: number | null;
  startedAt: string;
}

interface CronJob {
  id: string;
  name: string;
  schedule: string;
  path: string;
  timeoutMs: number;
  enabled: boolean;
  createdAt: string;
  lastRun: LastRun | null;
}

interface CronRun {
  id: string;
  trigger: "scheduled" | "manual";
  outcome: RunOutcome;
  httpStatus: number | null;
  durationMs: number | null;
  error: string | null;
  startedAt: string;
  finishedAt: string | null;
}

interface JobsResponse {
  productionState: ProductionState;
  jobs: CronJob[];
}

/**
 * Cron tab — scheduled HTTP requests fired at the project's production
 * deployment. Lists jobs with last-run status, lets the operator create,
 * toggle, trigger, inspect runs, and delete jobs.
 */
export function CronJobs() {
  const { org, project } = useParams<{ org: string; project: string }>();
  const qc = useQueryClient();
  const [openRunsId, setOpenRunsId] = useState<string | null>(null);

  const jobs = useQuery({
    queryKey: ["cron-jobs", org, project],
    queryFn: () => api.get<JobsResponse>(`/orgs/${org}/projects/${project}/cron-jobs`),
    refetchInterval: 30_000,
  });

  const toggleEnabled = useMutation({
    mutationFn: ({ id, enabled }: { id: string; enabled: boolean }) =>
      api.patch(`/orgs/${org}/projects/${project}/cron-jobs/${id}`, { enabled }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["cron-jobs", org, project] }),
  });
  const runNow = useMutation({
    mutationFn: (id: string) => api.post(`/orgs/${org}/projects/${project}/cron-jobs/${id}/run`),
    onSuccess: (_r, id) => {
      qc.invalidateQueries({ queryKey: ["cron-jobs", org, project] });
      qc.invalidateQueries({ queryKey: ["cron-runs", org, project, id] });
    },
  });
  const remove = useMutation({
    mutationFn: (id: string) => api.delete(`/orgs/${org}/projects/${project}/cron-jobs/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["cron-jobs", org, project] }),
  });

  const productionState = jobs.data?.productionState;

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-base font-semibold inline-flex items-center gap-2">
          <Clock className="h-3.5 w-3.5 text-muted-foreground" /> Cron jobs
        </h2>
        <p className="text-xs text-muted-foreground mt-0.5">
          Scheduled requests to your production deployment
        </p>
      </div>

      {productionState && productionState !== "ok" && (
        <div className="bg-attention-subtle text-attention border border-attention/30 rounded-md px-3 py-2 text-xs">
          {productionState === "no_production"
            ? "Inactive — this project has no production deployment yet."
            : "Inactive — production is a static build with no server to call."}
        </div>
      )}

      <CreateJobForm
        orgSlug={org!}
        projectSlug={project!}
        onCreated={() => qc.invalidateQueries({ queryKey: ["cron-jobs", org, project] })}
      />

      <div className="of-card">
        <div className="of-card-header">
          <h3 className="text-sm font-semibold inline-flex items-center gap-1.5">
            <Clock className="h-3.5 w-3.5 text-muted-foreground" /> Jobs
          </h3>
          <span className="text-2xs text-subtle-foreground">{jobs.data?.jobs.length ?? 0} / 10</span>
        </div>
        {jobs.isLoading && <Empty>Loading…</Empty>}
        {!jobs.isLoading && (jobs.data?.jobs.length ?? 0) === 0 && (
          <Empty>
            No cron jobs yet. Add one above — e.g. <span className="font-mono">0 3 * * *</span> hitting{" "}
            <span className="font-mono">/api/cron/cleanup</span>.
          </Empty>
        )}
        {jobs.data?.jobs.map((j) => (
          <div key={j.id} className="border-b border-border-muted last:border-b-0">
            <div className="of-row gap-3 items-start flex-wrap sm:flex-nowrap">
              <div className="min-w-0 basis-full sm:basis-auto sm:flex-1">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-semibold truncate">{j.name}</span>
                  {!j.enabled && (
                    <span className="text-2xs uppercase tracking-wider bg-subtle text-subtle-foreground border border-border-muted rounded px-1.5 py-0.5">
                      paused
                    </span>
                  )}
                </div>
                <div className="font-mono text-xs text-muted-foreground mt-0.5 truncate">
                  {j.schedule} · {j.path}
                </div>
                <div className="flex flex-wrap items-center gap-2 mt-1">
                  {j.lastRun ? (
                    <>
                      <OutcomeBadge outcome={j.lastRun.outcome} />
                      {j.lastRun.httpStatus != null && (
                        <span className="text-2xs font-mono text-subtle-foreground tabular-nums">{j.lastRun.httpStatus}</span>
                      )}
                      {j.lastRun.durationMs != null && (
                        <span className="text-2xs font-mono text-subtle-foreground tabular-nums">{fmtDuration(j.lastRun.durationMs)}</span>
                      )}
                      <RelativeTime value={j.lastRun.startedAt} />
                    </>
                  ) : (
                    <span className="text-2xs text-subtle-foreground">Never run</span>
                  )}
                </div>
              </div>
              <label className="text-xs flex items-center gap-1 cursor-pointer pt-1">
                <input
                  type="checkbox"
                  checked={j.enabled}
                  disabled={toggleEnabled.isPending}
                  onChange={(e) => toggleEnabled.mutate({ id: j.id, enabled: e.target.checked })}
                />
                enabled
              </label>
              <Button
                variant="ghost"
                size="icon"
                title="Run now"
                onClick={() => runNow.mutate(j.id)}
                disabled={runNow.isPending}
              >
                {runNow.isPending && runNow.variables === j.id ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                ) : (
                  <Play className="h-3.5 w-3.5" />
                )}
              </Button>
              <Button
                variant="ghost"
                size="icon"
                title="Toggle runs"
                onClick={() => setOpenRunsId(openRunsId === j.id ? null : j.id)}
              >
                {openRunsId === j.id ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
              </Button>
              <Button
                variant="ghost"
                size="icon"
                className="text-danger"
                disabled={remove.isPending}
                onClick={() => {
                  if (window.confirm(`Delete cron job '${j.name}'? Its run history goes with it.`)) remove.mutate(j.id);
                }}
              >
                {remove.isPending && remove.variables === j.id ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                ) : (
                  <Trash2 className="h-3.5 w-3.5" />
                )}
              </Button>
            </div>
            {runNow.error instanceof ApiError && runNow.variables === j.id && (
              <div className="px-4 pb-2 text-xs text-danger">{runNow.error.message}</div>
            )}
            {openRunsId === j.id && <RunsLog orgSlug={org!} projectSlug={project!} jobId={j.id} />}
          </div>
        ))}
      </div>
    </div>
  );
}

function RunsLog({ orgSlug, projectSlug, jobId }: { orgSlug: string; projectSlug: string; jobId: string }) {
  const q = useQuery({
    queryKey: ["cron-runs", orgSlug, projectSlug, jobId],
    queryFn: () =>
      api
        .get<{ runs: CronRun[] }>(`/orgs/${orgSlug}/projects/${projectSlug}/cron-jobs/${jobId}/runs`)
        .then((r) => r.runs),
    refetchInterval: 10_000,
  });

  return (
    <div className="bg-subtle/50 border-t border-border-muted px-4 py-2.5 overflow-x-auto">
      {q.isLoading && <div className="text-2xs text-muted-foreground">Loading runs…</div>}
      {!q.isLoading && (q.data?.length ?? 0) === 0 && (
        <div className="text-2xs text-muted-foreground">No runs yet — trigger one with Run now.</div>
      )}
      <div className="space-y-1">
        {q.data?.map((r) => (
          <div key={r.id} className="flex items-center gap-2 text-2xs font-mono whitespace-nowrap">
            <span className="w-16 shrink-0 text-subtle-foreground">{r.trigger}</span>
            <OutcomeBadge outcome={r.outcome} />
            <span className="w-10 shrink-0 tabular-nums text-subtle-foreground">{r.httpStatus ?? "—"}</span>
            <span className="w-14 shrink-0 tabular-nums text-subtle-foreground">
              {r.durationMs != null ? fmtDuration(r.durationMs) : "—"}
            </span>
            <span className="flex-1 truncate text-subtle-foreground">{r.error ?? ""}</span>
            <RelativeTime value={r.startedAt} />
          </div>
        ))}
      </div>
    </div>
  );
}

function CreateJobForm({ orgSlug, projectSlug, onCreated }: {
  orgSlug: string;
  projectSlug: string;
  onCreated: () => void;
}) {
  const [name, setName] = useState("");
  const [schedule, setSchedule] = useState("");
  const [path, setPath] = useState("");
  const [timeoutSec, setTimeoutSec] = useState("30");
  const seconds = Number(timeoutSec);
  const timeoutValid = Number.isInteger(seconds) && seconds >= 1 && seconds <= 300;

  const create = useMutation({
    mutationFn: () =>
      api.post<{ job: CronJob }>(`/orgs/${orgSlug}/projects/${projectSlug}/cron-jobs`, {
        name,
        schedule,
        path,
        timeoutMs: seconds * 1000,
      }),
    onSuccess: () => {
      onCreated();
      setName("");
      setSchedule("");
      setPath("");
      setTimeoutSec("30");
    },
  });

  return (
    <details className="of-card">
      <summary className="of-card-header cursor-pointer list-none">
        <h3 className="text-sm font-semibold inline-flex items-center gap-1.5">
          <Plus className="h-3.5 w-3.5" /> Add cron job
        </h3>
      </summary>
      <div className="px-4 py-3 space-y-2">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
          <div className="space-y-1">
            <Label htmlFor="cron-name" className="text-xs">Name</Label>
            <Input
              id="cron-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="nightly-cleanup"
              className="font-mono text-sm"
            />
            <div className="text-2xs text-subtle-foreground">letters, digits, - and _</div>
          </div>
          <div className="space-y-1">
            <Label htmlFor="cron-schedule" className="text-xs">Schedule</Label>
            <Input
              id="cron-schedule"
              value={schedule}
              onChange={(e) => setSchedule(e.target.value)}
              placeholder="0 3 * * *"
              className="font-mono text-sm"
            />
            <div className="text-2xs text-subtle-foreground">e.g. 0 3 * * * — five fields, UTC</div>
          </div>
          <div className="space-y-1">
            <Label htmlFor="cron-path" className="text-xs">Path</Label>
            <Input
              id="cron-path"
              value={path}
              onChange={(e) => setPath(e.target.value)}
              placeholder="/api/cron/cleanup"
              className="font-mono text-sm"
            />
            <div className="text-2xs text-subtle-foreground">requested on the production deployment</div>
          </div>
          <div className="space-y-1">
            <Label htmlFor="cron-timeout" className="text-xs">Timeout (seconds)</Label>
            <Input
              id="cron-timeout"
              type="number"
              min={1}
              max={300}
              value={timeoutSec}
              onChange={(e) => setTimeoutSec(e.target.value)}
              className="font-mono text-sm"
            />
            <div className="text-2xs text-subtle-foreground">1–300, default 30</div>
          </div>
        </div>
        {create.error instanceof ApiError && <div className="text-xs text-danger">{create.error.message}</div>}
        <div className="flex justify-end">
          <Button
            variant="primary"
            size="sm"
            onClick={() => create.mutate()}
            disabled={!name || !schedule || !path || !timeoutValid || create.isPending}
          >
            {create.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Plus className="h-3.5 w-3.5" />}
            Add cron job
          </Button>
        </div>
      </div>
    </details>
  );
}

function OutcomeBadge({ outcome }: { outcome: RunOutcome }) {
  const cls =
    outcome === "success"
      ? "bg-success-subtle text-success border-success/30"
      : outcome === "failed" || outcome === "timeout"
        ? "bg-danger-subtle text-danger border-danger/30"
        : "bg-subtle text-subtle-foreground border-border-muted";
  return (
    <span className={`text-2xs uppercase tracking-wider border rounded px-1.5 py-0.5 ${cls}`}>
      {outcome.replace(/_/g, " ")}
    </span>
  );
}

function fmtDuration(ms: number): string {
  if (ms < 1000) return `${ms}ms`;
  return `${(ms / 1000).toFixed(1)}s`;
}

function Empty({ children }: { children: React.ReactNode }) {
  return <div className="px-4 py-10 text-center text-sm text-muted-foreground">{children}</div>;
}
